'use client';

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { TABLET_BREAKPOINT } from '@/configs/layout';
import { CONFIG_MENU_OPEN, SIDEBAR_EXPANDED } from '@/configs/storageKeys';

interface Result {
  configMenuOpen: boolean;
  isMobile: boolean;
  sidebarExpanded: boolean;
  setConfigMenuOpen: (open: boolean) => void;
  setSidebarExpanded: (expanded: boolean) => void;
}

function getDefaultResult() {
  return {
    configMenuOpen: getDefaultConfigMenuOpen(),
    isMobile: false,
    sidebarExpanded: getDefaultSidebarExpanded(),
    setConfigMenuOpen: () => {},
    setSidebarExpanded: () => {},
  };
}

function getDefaultConfigMenuOpen() {
  if (typeof window === 'undefined') return true;

  const sidebarExpanded = getDefaultSidebarExpanded();
  const stored = window.localStorage.getItem(CONFIG_MENU_OPEN);
  if (stored === null) return sidebarExpanded;
  return (
    sidebarExpanded && window.localStorage.getItem(CONFIG_MENU_OPEN) === 'true'
  );
}

function getDefaultSidebarExpanded() {
  if (typeof window === 'undefined') return true;

  const stored = window.localStorage.getItem(SIDEBAR_EXPANDED);
  if (stored === null) return true;
  return stored === 'true';
}

const ShellContext = createContext<Result>(getDefaultResult());

export function useShell() {
  return useContext(ShellContext);
}

export function ShellContextProvider({ children }: PropsWithChildren) {
  const [contextData, setContextData] = useState<Result>(getDefaultResult);

  const setConfigMenuOpen = useCallback((value: boolean) => {
    setContextData((prev) => {
      if (prev.configMenuOpen === value) {
        return prev;
      }
      localStorage.setItem(CONFIG_MENU_OPEN, String(value));
      return { ...prev, configMenuOpen: value };
    });
  }, []);

  const setSidebarExpanded = useCallback((value: boolean) => {
    setContextData((prev) => {
      if (prev.sidebarExpanded === value) {
        return prev;
      }
      localStorage.setItem(SIDEBAR_EXPANDED, String(value));
      return { ...prev, sidebarExpanded: value };
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = (event: MediaQueryListEvent) => {
      setContextData((prev) => {
        if (prev.isMobile !== event.matches) {
          return { ...prev, isMobile: event.matches };
        }
        return prev;
      });
    };

    const mediaQueryList = window.matchMedia(
      `(max-width: ${TABLET_BREAKPOINT}px)`,
    );
    mediaQueryList.addEventListener('change', handleResize);

    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs initial isMobile from the matchMedia result after subscribing to the media-query change listener (media-query subscription)
    setContextData((prev) => {
      if (prev.isMobile !== mediaQueryList.matches) {
        return { ...prev, isMobile: mediaQueryList.matches };
      }
      return prev;
    });

    return () => {
      mediaQueryList.removeEventListener('change', handleResize);
    };
  }, []);

  const contextValue = useMemo<Result>(
    () => ({ ...contextData, setConfigMenuOpen, setSidebarExpanded }),
    [contextData, setConfigMenuOpen, setSidebarExpanded],
  );

  return (
    <ShellContext.Provider value={contextValue}>
      {children}
    </ShellContext.Provider>
  );
}
